import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Award, ChevronLeft, Filter, Search, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

interface UserSkill {
  userId: string;
  userName: string;
  team: string;
  role: string;
  skills: {
    category: string;
    skillName: string;
    level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
    certified: boolean;
    certificationExpiry?: string;
    lastAssessed: string;
  }[];
}

const MOCK_SKILLS_DATA: UserSkill[] = [
  {
    userId: '1',
    userName: 'Andy Morris',
    team: 'Wastewater Treatment',
    role: 'Engineer',
    skills: [
      {
        category: 'Technical',
        skillName: 'Gas Service Installation',
        level: 'Expert',
        certified: true,
        certificationExpiry: '2026-12-31',
        lastAssessed: '2026-01-15',
      },
      {
        category: 'Safety',
        skillName: 'CSCS Card',
        level: 'Advanced',
        certified: true,
        certificationExpiry: '2027-06-30',
        lastAssessed: '2025-11-20',
      },
      {
        category: 'Equipment',
        skillName: 'Excavator Operation',
        level: 'Advanced',
        certified: true,
        certificationExpiry: '2026-09-15',
        lastAssessed: '2026-02-10',
      },
    ],
  },
  {
    userId: '2',
    userName: 'Sarah Johnson',
    team: 'Field Services',
    role: 'Supervisor',
    skills: [
      {
        category: 'Leadership',
        skillName: 'Team Management',
        level: 'Expert',
        certified: false,
        lastAssessed: '2026-03-01',
      },
      {
        category: 'Technical',
        skillName: 'Built-over Service Relay',
        level: 'Advanced',
        certified: true,
        certificationExpiry: '2026-11-20',
        lastAssessed: '2026-01-10',
      },
      {
        category: 'Safety',
        skillName: 'First Aid',
        level: 'Intermediate',
        certified: true,
        certificationExpiry: '2026-08-15',
        lastAssessed: '2025-12-05',
      },
    ],
  },
  {
    userId: '3',
    userName: 'Mike Davies',
    team: 'Operations',
    role: 'Technician',
    skills: [
      {
        category: 'Technical',
        skillName: 'GSR Service Work',
        level: 'Intermediate',
        certified: true,
        certificationExpiry: '2026-10-30',
        lastAssessed: '2026-02-20',
      },
      {
        category: 'Safety',
        skillName: 'Manual Handling',
        level: 'Advanced',
        certified: true,
        certificationExpiry: '2027-03-15',
        lastAssessed: '2026-01-25',
      },
      {
        category: 'Technical',
        skillName: 'Service Disconnection',
        level: 'Beginner',
        certified: false,
        lastAssessed: '2026-03-10',
      },
    ],
  },
  {
    userId: '4',
    userName: 'Lisa Parker',
    team: 'Quality Control',
    role: 'Engineer',
    skills: [
      {
        category: 'Technical',
        skillName: 'Quality Inspection',
        level: 'Expert',
        certified: true,
        certificationExpiry: '2026-12-01',
        lastAssessed: '2026-02-15',
      },
      {
        category: 'Technical',
        skillName: 'Survey & Assessment',
        level: 'Advanced',
        certified: true,
        certificationExpiry: '2027-01-20',
        lastAssessed: '2026-01-30',
      },
      {
        category: 'Safety',
        skillName: 'Risk Assessment',
        level: 'Expert',
        certified: false,
        lastAssessed: '2026-03-05',
      },
    ],
  },
  {
    userId: '5',
    userName: 'Jim Smith',
    team: 'Emergency Response',
    role: 'Operator',
    skills: [
      {
        category: 'Technical',
        skillName: 'Emergency Gas Shutoff',
        level: 'Expert',
        certified: true,
        certificationExpiry: '2026-07-15',
        lastAssessed: '2026-02-01',
      },
      {
        category: 'Safety',
        skillName: 'Confined Space Entry',
        level: 'Advanced',
        certified: true,
        certificationExpiry: '2026-06-30',
        lastAssessed: '2025-12-20',
      },
      {
        category: 'Equipment',
        skillName: 'HGV Class 2',
        level: 'Intermediate',
        certified: true,
        certificationExpiry: '2028-04-10',
        lastAssessed: '2026-01-05',
      },
    ],
  },
];

const SKILL_CATEGORIES = ['All Categories', 'Technical', 'Safety', 'Equipment', 'Leadership'];
const TEAMS = [
  'All Teams',
  'Wastewater Treatment',
  'Field Services',
  'Operations',
  'Quality Control',
  'Emergency Response',
];

export default function SkillsMatrixPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedTeam, setSelectedTeam] = useState('All Teams');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');

  const filteredData = MOCK_SKILLS_DATA.filter((user) => {
    const matchesSearch =
      searchQuery === '' ||
      user.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.skills.some((s) => s.skillName.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesTeam = selectedTeam === 'All Teams' || user.team === selectedTeam;

    const matchesCategory =
      selectedCategory === 'All Categories' ||
      user.skills.some((s) => s.category === selectedCategory);

    const matchesLevel =
      selectedLevel === 'All Levels' || user.skills.some((s) => s.level === selectedLevel);

    return matchesSearch && matchesTeam && matchesCategory && matchesLevel;
  });

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Expert':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Advanced':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Intermediate':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'Beginner':
        return 'bg-slate-100 text-slate-800 border-slate-300';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  const isCertificationExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return expiry <= threeMonthsFromNow;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Skills Matrix"
        subtitle={`${filteredData.length} users • ${filteredData.reduce((sum, u) => sum + u.skills.length, 0)} skills tracked`}
        icon={<Award className="w-7 h-7" style={{ color: '#E84A2B' }} />}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate('/users')}
            className="border-border hover:bg-accent rounded-lg"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Users
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 space-y-4">
        {/* Filters */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
              <Input
                placeholder="Search users or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 bg-background border-border"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-[200px] rounded-lg border-border">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                {TEAMS.map((team) => (
                  <SelectItem key={team} value={team}>
                    {team}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px] rounded-lg border-border">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                {SKILL_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLevel} onValueChange={setSelectedLevel}>
              <SelectTrigger className="w-[160px] rounded-lg border-border">
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All Levels">All Levels</SelectItem>
                <SelectItem value="Expert">Expert</SelectItem>
                <SelectItem value="Advanced">Advanced</SelectItem>
                <SelectItem value="Intermediate">Intermediate</SelectItem>
                <SelectItem value="Beginner">Beginner</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Skills Table */}
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">User</TableHead>
                <TableHead className="text-muted-foreground">Team / Role</TableHead>
                <TableHead className="text-muted-foreground">Skills</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((user) => (
                <TableRow key={user.userId} className="border-border hover:bg-accent/50">
                  <TableCell>
                    <div className="font-semibold text-foreground">{user.userName}</div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm text-foreground">{user.team}</div>
                      <div className="text-xs text-muted-foreground">{user.role}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      {user.skills.map((skill, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 p-2 rounded-lg bg-muted/30"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-foreground">
                                {skill.skillName}
                              </span>
                              {skill.certified && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${
                                    isCertificationExpiringSoon(skill.certificationExpiry)
                                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                                      : 'bg-green-100 text-green-800 border-green-300'
                                  }`}
                                >
                                  {isCertificationExpiringSoon(skill.certificationExpiry)
                                    ? '⚠️ Certified'
                                    : '✓ Certified'}
                                </Badge>
                              )}
                              <Badge
                                variant="outline"
                                className={`text-xs ${getLevelColor(skill.level)}`}
                              >
                                {skill.level}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{skill.category}</span>
                              <span>•</span>
                              <span>Assessed: {skill.lastAssessed}</span>
                              {skill.certificationExpiry && (
                                <>
                                  <span>•</span>
                                  <span
                                    className={
                                      isCertificationExpiringSoon(skill.certificationExpiry)
                                        ? 'text-amber-600 font-medium'
                                        : ''
                                    }
                                  >
                                    Expires: {skill.certificationExpiry}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredData.length === 0 && (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <div className="text-center">
                <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No skills found matching your filters</p>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Total Users</div>
            <div className="text-2xl font-bold text-foreground">{MOCK_SKILLS_DATA.length}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Total Skills</div>
            <div className="text-2xl font-bold text-foreground">
              {MOCK_SKILLS_DATA.reduce((sum, u) => sum + u.skills.length, 0)}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Certified Skills</div>
            <div className="text-2xl font-bold text-green-600">
              {MOCK_SKILLS_DATA.reduce(
                (sum, u) => sum + u.skills.filter((s) => s.certified).length,
                0,
              )}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Expiring Soon</div>
            <div className="text-2xl font-bold text-amber-600">
              {MOCK_SKILLS_DATA.reduce(
                (sum, u) =>
                  sum +
                  u.skills.filter((s) => isCertificationExpiringSoon(s.certificationExpiry)).length,
                0,
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
